import {QUESTION_TYPE} from '../../../../utils/constants'
import {utils as XLSXUtils, writeFile as writeXlsxFile} from 'xlsx'
import jsPDF from 'jspdf'

const AUDIENCE_TEXT_BY_VALUE = {
  1: 'Staff',
  2: 'Parent',
  4: 'Student',
}

function getQuestionTypeLabel(questionType) {
  if (questionType === QUESTION_TYPE.NUMBER_SLIDER) return 'Number Slider'
  if (questionType === QUESTION_TYPE.PERCENTAGE) return 'Percentage'
  if (questionType === QUESTION_TYPE.LIKERT) return 'Likert Scale'
  if (questionType === QUESTION_TYPE.COMMENTS) return 'Comments'
  if (questionType === QUESTION_TYPE.MULTIPLE_CHOICE) return 'Multiple Choice'
  if (questionType === QUESTION_TYPE.MULTIPLE_SELECT) return 'Multiple Select'
  if (questionType === QUESTION_TYPE.YES_NO) return 'Yes/No'
  if (questionType === QUESTION_TYPE.YES_NO_MAYBE) return 'Yes/No/Maybe'
  if (questionType === QUESTION_TYPE.RANKING) return 'Ranking'
  return 'Other'
}

function getAudienceQuestionText(question, audienceValue) {
  if (audienceValue === 1) return question?.staffText || ''
  if (audienceValue === 2) return question?.parentText || ''
  return question?.pupilText || ''
}

function getAudienceOptionText(option, audienceValue) {
  if (audienceValue === 1) return option?.staffText || ''
  if (audienceValue === 2) return option?.parentText || ''
  return option?.pupilText || ''
}

function getOptionsForAudience(question, audienceValue) {
  const list = Array.isArray(question?.multiOptionResponseAttributes)
    ? [...question.multiOptionResponseAttributes]
    : []
  return list
    .sort((a, b) => (a?.order || 0) - (b?.order || 0))
    .map(option => ({
      text: getAudienceOptionText(option, audienceValue),
      score: option?.numValue ?? '',
    }))
}

function getFollowupsForAudience(question, audienceValue) {
  const followups = Array.isArray(question?.followupQuestions)
    ? question.followupQuestions
    : []

  return followups.map(followup => {
    const followupText =
      audienceValue === 1
        ? followup?.staffFollowupText
        : audienceValue === 2
          ? followup?.parentFollowupText
          : followup?.pupilFollowupText

    const followupCondition =
      question?.questionType === QUESTION_TYPE.NUMBER_SLIDER ||
      question?.questionType === QUESTION_TYPE.PERCENTAGE
        ? followup?.followupValue
        : getAudienceOptionText(
            (question?.multiOptionResponseAttributes || []).find(
              x => x?.value === followup?.followupValue
            ) || {},
            audienceValue
          )

    return {
      question: followupText || '',
      condition: followupCondition || '',
    }
  })
}

function getCategoryName(question) {
  return question?.categoryName || question?.CategoryName || question?.categoryname || ''
}

function escapeCsvCell(value) {
  const text = value == null ? '' : String(value)
  return `"${text.replace(/"/g, '""')}"`
}

function downloadBlob(content, mimeType, filename) {
  const blob = new Blob([content], {type: mimeType})
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

export function buildExportRows({questions = [], audiences = []}) {
  const rows = []
  audiences.forEach(aud => {
    questions.forEach((question, index) => {
      const options = getOptionsForAudience(question, aud.value)
      const followups = getFollowupsForAudience(question, aud.value)
      rows.push({
        audience: aud.label || AUDIENCE_TEXT_BY_VALUE[aud.value] || '',
        questionNo: index + 1,
        questionText: getAudienceQuestionText(question, aud.value),
        questionType: getQuestionTypeLabel(question?.questionType),
        categoryName: getCategoryName(question),
        options: options.map(x => x.text).filter(Boolean).join(' | '),
        optionScores: options
          .map(x => (x.score === '' ? '' : `${x.text || '-'}: ${x.score}`))
          .filter(Boolean)
          .join(' | '),
        followupQuestions: followups.map(x => x.question).filter(Boolean).join(' | '),
        followupConditions: followups.map(x => x.condition).filter(Boolean).join(' | '),
      })
    })
  })
  return rows
}

export function buildPdfSections({questions = [], audiences = []}) {
  return audiences.map(aud => ({
    audienceLabel: aud.label || AUDIENCE_TEXT_BY_VALUE[aud.value] || '',
    questions: questions.map((question, index) => ({
      questionNo: index + 1,
      questionText: getAudienceQuestionText(question, aud.value),
      questionType: getQuestionTypeLabel(question?.questionType),
      categoryName: getCategoryName(question),
      options: getOptionsForAudience(question, aud.value),
      followups: getFollowupsForAudience(question, aud.value),
    })),
  }))
}

export function downloadCsv({rows = [], fileBaseName = 'survey-questions'}) {
  const headers = [
    'Audience',
    'Question No',
    'Question',
    'Question Type',
    'Category',
    'Options',
    'Option Scores',
    'Follow-up Questions',
    'Follow-up Conditions',
  ]
  const csvLines = [
    headers.map(escapeCsvCell).join(','),
    ...rows.map(row =>
      [
        row.audience,
        row.questionNo,
        row.questionText,
        row.questionType,
        row.categoryName,
        row.options,
        row.optionScores,
        row.followupQuestions,
        row.followupConditions,
      ]
        .map(escapeCsvCell)
        .join(',')
    ),
  ]
  downloadBlob(
    '\uFEFF' + csvLines.join('\n'),
    'text/csv;charset=utf-8;',
    `${fileBaseName}.csv`
  )
}

export function downloadExcel({rows = [], fileBaseName = 'survey-questions'}) {
  const worksheetRows = rows.map(row => ({
    Audience: row.audience,
    'Question No': row.questionNo,
    Question: row.questionText,
    'Question Type': row.questionType,
    Category: row.categoryName,
    Options: row.options,
    'Option Scores': row.optionScores,
    'Follow-up Questions': row.followupQuestions,
    'Follow-up Conditions': row.followupConditions,
  }))
  const worksheet = XLSXUtils.json_to_sheet(worksheetRows)
  const workbook = XLSXUtils.book_new()
  XLSXUtils.book_append_sheet(workbook, worksheet, 'Questions')
  writeXlsxFile(workbook, `${fileBaseName}.xlsx`)
}

export function downloadStyledPdf({
  sections = [],
  fileBaseName = 'survey-questions',
  surveyName = 'Survey Questions',
}) {
  const doc = new jsPDF({orientation: 'p', unit: 'pt', format: 'a4'})
  const pageWidth = doc.internal.pageSize.getWidth()
  const pageHeight = doc.internal.pageSize.getHeight()
  const marginX = 30
  const cardPadding = 10
  const cardWidth = pageWidth - marginX * 2
  const cardFillColor = [238, 248, 252]
  const cardBorderColor = [135, 232, 252]
  const badgeFillColor = [255, 255, 255]
  const badgeBorderColor = [220, 220, 220]
  const tableHeaderFillColor = [69, 51, 140]
  const tableGridColor = [210, 210, 210]
  const contentWidth = cardWidth - cardPadding * 2
  let cursorY = 36

  const ensureSpace = neededHeight => {
    if (cursorY + neededHeight > pageHeight - 30) {
      doc.addPage()
      cursorY = 36
    }
  }

  const splitLines = (text, width) => doc.splitTextToSize(String(text || ''), width)

  const getQuestionBlockHeight = question => {
    let height = cardPadding + 6

    if (question.categoryName) {
      height += 18
    }

    doc.setFontSize(11)
    doc.setFont('helvetica', 'bold')
    const title = `Question ${question.questionNo}: ${question.questionText || ''}`
    const titleLines = splitLines(title, contentWidth)
    height += titleLines.length * 13

    height += 12 // Question type line + spacing

    const hasOptions = Array.isArray(question.options) && question.options.length > 0
    if (hasOptions) {
      const tableHeaderHeight = 16
      const tableRowHeight = 16
      height += tableHeaderHeight + question.options.length * tableRowHeight + 8
    } else {
      height += 12
    }

    if (question.followups.length > 0) {
      height += 10
      question.followups.forEach(f => {
        const lines = splitLines(
          `- ${f.question}${f.condition ? ` (Condition: ${f.condition})` : ''}`,
          contentWidth - 6
        )
        height += lines.length * 10
      })
      height += 4
    }

    height += 8 + cardPadding
    return Math.max(120, height)
  }

  doc.setFontSize(16)
  doc.setFont('helvetica', 'bold')
  const surveyTitleLines = splitLines(
    `${surveyName} - View All Questions`,
    pageWidth - marginX * 2
  )
  doc.text(surveyTitleLines, marginX, cursorY)
  cursorY += surveyTitleLines.length * 18

  sections.forEach((section, sectionIndex) => {
    ensureSpace(26)
    doc.setFontSize(12)
    doc.setTextColor(0, 122, 122)
    doc.setFont('helvetica', 'bold')
    doc.text(String(section.audienceLabel || ''), marginX, cursorY)
    doc.setTextColor(0, 0, 0)
    cursorY += 14

    section.questions.forEach(question => {
      const blockHeight = getQuestionBlockHeight(question)
      ensureSpace(blockHeight + 8)
      const cardTop = cursorY

      doc.setFillColor(...cardFillColor)
      doc.setDrawColor(...cardBorderColor)
      doc.rect(marginX, cardTop, cardWidth, blockHeight, 'FD')

      let textY = cardTop + cardPadding + 6
      if (question.categoryName) {
        doc.setFontSize(9)
        doc.setFont('helvetica', 'normal')
        doc.setFillColor(...badgeFillColor)
        doc.setDrawColor(...badgeBorderColor)
        const badgeText = question.categoryName
        const badgeWidth = Math.min(
          220,
          doc.getTextWidth(badgeText) + 12
        )
        doc.rect(marginX + cardPadding, textY - 8, badgeWidth, 14, 'FD')
        doc.text(badgeText, marginX + cardPadding + 6, textY + 2)
        textY += 18
      }

      doc.setFontSize(11)
      doc.setFont('helvetica', 'bold')
      const title = `Question ${question.questionNo}: ${question.questionText || ''}`
      const titleLines = splitLines(title, contentWidth)
      doc.text(titleLines, marginX + cardPadding, textY)
      textY += titleLines.length * 13

      doc.setFont('helvetica', 'bold')
      doc.text(`Question Type: ${question.questionType}`, marginX + cardPadding, textY)
      textY += 10

      const hasOptions = Array.isArray(question.options) && question.options.length > 0
      if (hasOptions) {
        const tableX = marginX + cardPadding
        const tableY = textY + 6
        const tableWidth = contentWidth
        const col1Width = Math.floor(tableWidth * 0.62)
        const col2Width = tableWidth - col1Width
        const headerHeight = 16
        const rowHeight = 16

        doc.setFillColor(...tableHeaderFillColor)
        doc.setDrawColor(...tableGridColor)
        doc.rect(tableX, tableY, tableWidth, headerHeight, 'FD')
        doc.setTextColor(255, 255, 255)
        doc.setFontSize(9)
        doc.setFont('helvetica', 'bold')
        doc.text('Answer Options', tableX + 6, tableY + 11)
        doc.text('Num Score', tableX + col1Width + 6, tableY + 11)
        doc.setTextColor(0, 0, 0)

        let rowY = tableY + headerHeight
        doc.setFont('helvetica', 'normal')
        question.options.forEach(option => {
          doc.setDrawColor(...tableGridColor)
          doc.rect(tableX, rowY, col1Width, rowHeight, 'S')
          doc.rect(tableX + col1Width, rowY, col2Width, rowHeight, 'S')
          doc.text(String(option.text || ''), tableX + 6, rowY + 11)
          doc.text(String(option.score ?? ''), tableX + col1Width + 6, rowY + 11)
          rowY += rowHeight
        })

        textY = rowY + 8
      } else {
        textY += 12
      }

      if (question.followups.length > 0) {
        doc.setFont('helvetica', 'bold')
        doc.setFontSize(9)
        doc.text('Follow-up Questions:', marginX + cardPadding, textY)
        textY += 10
        doc.setFont('helvetica', 'normal')
        question.followups.forEach(f => {
          const followupLines = splitLines(
            `- ${f.question}${f.condition ? ` (Condition: ${f.condition})` : ''}`,
            contentWidth - 6
          )
          doc.text(followupLines, marginX + cardPadding + 6, textY)
          textY += followupLines.length * 10
        })
      }
      cursorY = cardTop + blockHeight + 10
    })

    if (sectionIndex < sections.length - 1) {
      ensureSpace(20)
      cursorY += 4
    }
  })

  doc.save(`${fileBaseName}.pdf`)
}
